import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { categoryHead } from "@/components/CategoryLanding";
import { PRODUCTS_QUERY, storefrontApiRequest, type ShopifyProduct } from "@/lib/shopify";

const SLUG = "montessori-lelut";
const H1 = "Montessori-lelut — puulelut ja pehmoleikkisetit";
const TITLE = "Montessori Lelut | Wooden Story & Trixie Baby | Montessori24";
const DESC =
  "Uusi Montessori-lelujen osasto: puulelut Wooden Storyltä, Trixie Baby -aktiviteettilelut ja luonnonmateriaalit vauvasta esikouluikään. Ilmainen toimitus Suomeen 3–7 pv.";

// Storefront API vendor filter — only the newly added toy vendors that are on
// every sales channel (Online Store included) and were not previously
// surfaced in a dedicated collection on the site.
const VENDOR_QUERY =
  'vendor:"Wooden Story" OR vendor:"Trixie Baby" OR vendor:"Woopie Let\'s Go Green"';

const SORT_IDS = ["uusimmat", "halvin", "kallein", "nimi"] as const;
type SortId = (typeof SORT_IDS)[number];
const PER_PAGE = 12;

// Optional (no `.default()`) so a naked `/montessori-lelut` doesn't
// 307-redirect to `/montessori-lelut?jarjesta=uusimmat&sivu=1` — see the
// same note in `kauppa.tsx`. Defaults applied at read time.
const searchSchema = z.object({
  jarjesta: fallback(z.enum(SORT_IDS).optional(), undefined),
  sivu: fallback(z.number().int().optional(), undefined),
});

const toysQueryOptions = queryOptions({
  queryKey: ["products", "montessori-lelut"] as const,
  queryFn: async () => {
    const res = await storefrontApiRequest(PRODUCTS_QUERY, {
      first: 100,
      query: VENDOR_QUERY,
    });
    return (res?.data?.products?.edges ?? []) as ShopifyProduct[];
  },
});

const FAQS = [
  {
    q: "Mitä Montessori Lelut -osastossa on?",
    a: "Puuleluja Wooden Storyn ja Trixie Babyn valikoimasta: helistimiä, palapelejä, pinoamis- ja lajittelupelejä, aktiviteettikärryjä sekä puisia roolileikkisettejä. Kaikki on valittu Montessori-pedagogiikan periaatteilla — yksi lelu, yksi selkeä taito.",
  },
  {
    q: "Mistä iästä lähtien lelut sopivat?",
    a: "Vauvan puiset helistimet (Wooden Rattle) 3 kk ylöspäin, pujotus- ja pinoamislelut 10 kk lähtien, palapelit ja roolileikkisetit 2–6-vuotiaille.",
  },
  {
    q: "Ovatko lelut turvallisia?",
    a: "Ilmoitamme tuotekohtaiset turvallisuus-, materiaali- ja vaatimustenmukaisuustiedot jokaisella tuotesivulla — esimerkiksi ikäsuosituksen, materiaalit ja valmistajan ilmoittamat standardit. Tarkista aina kyseisen tuotteen omat tiedot.",
  },
  {
    q: "Toimitus ja palautus?",
    a: "Ilmainen toimitus Suomeen 3–7 arkipäivässä. Lakisääteinen 14 päivän peruuttamisoikeus ja lisäksi vapaaehtoinen 30 päivän palautusetu.",
  },
];

export const Route = createFileRoute("/montessori-lelut")({
  validateSearch: zodValidator(searchSchema),
  loader: ({ context }) => context.queryClient.ensureQueryData(toysQueryOptions),
  head: () => categoryHead({ slug: SLUG, title: TITLE, h1: H1, description: DESC, faqs: FAQS }),
  component: MontessoriLelutPage,
});

function MontessoriLelutPage() {
  const { data } = useSuspenseQuery(toysQueryOptions);
  const raw = Route.useSearch();
  const jarjesta: SortId = raw.jarjesta ?? "uusimmat";
  const sivu: number = raw.sivu ?? 1;
  const navigate = useNavigate({ from: "/montessori-lelut" });
  const all = data ?? [];

  const sorted = useMemo(() => {
    const arr = [...all];
    arr.sort((a, b) => {
      const pa = parseFloat(a.node.priceRange.minVariantPrice.amount);
      const pb = parseFloat(b.node.priceRange.minVariantPrice.amount);
      if (jarjesta === "halvin") return pa - pb;
      if (jarjesta === "kallein") return pb - pa;
      if (jarjesta === "nimi") return a.node.title.localeCompare(b.node.title, "fi");
      return 0; // "uusimmat" — preserve Shopify default order
    });
    return arr;
  }, [all, jarjesta]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PER_PAGE));
  const currentPage = Math.min(Math.max(1, sivu), totalPages);
  const start = (currentPage - 1) * PER_PAGE;
  const products = sorted.slice(start, start + PER_PAGE);

  const sortLabel: Record<SortId, string> = {
    uusimmat: "Uusimmat ensin",
    halvin: "Hinta: halvin ensin",
    kallein: "Hinta: kallein ensin",
    nimi: "Nimi (A–Ö)",
  };

  return (
    <Layout>
      <section className="container-prose pt-16 pb-12 md:pt-24">
        <nav aria-label="Murupolku" className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Etusivu</Link>
          <span className="mx-2">/</span>
          <Link to="/kauppa" className="hover:text-foreground">Kauppa</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Montessori Lelut</span>
        </nav>
        <span className="mt-6 block text-xs uppercase tracking-[0.25em] text-clay">Uusi osasto</span>
        <h1 className="mt-3 text-5xl text-foreground md:text-6xl text-balance">{H1}</h1>
        <p className="mt-5 max-w-2xl text-muted-foreground leading-relaxed">
          Puulelut Wooden Storyltä ja Trixie Babyltä — helistimistä palapeleihin ja roolileikkeihin. Luonnonmateriaaleista ja sukupolvelta toiselle kestäviä.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          <Link to="/pikler-kolmio" className="rounded-full border border-border px-4 py-1.5 text-sm text-muted-foreground hover:border-foreground hover:text-foreground">Pikler-kiipeilyteline</Link>
          <Link to="/uutuudet" className="rounded-full border border-border px-4 py-1.5 text-sm text-muted-foreground hover:border-foreground hover:text-foreground">Uutuudet</Link>
          <Link to="/kauppa" className="rounded-full border border-border px-4 py-1.5 text-sm text-muted-foreground hover:border-foreground hover:text-foreground">Koko kauppa</Link>
        </div>
      </section>

      <section className="container-prose pb-16">
        {sorted.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-cream py-16 text-center">
            <p className="text-foreground">Uudet lelut latautuvat pian.</p>
          </div>
        ) : (
          <>
            <div className="mb-8 flex flex-wrap items-center justify-between gap-4 border-b border-border/60 pb-4">
              <p className="text-sm text-muted-foreground">
                {sorted.length} {sorted.length === 1 ? "tuote" : "tuotetta"}
                {totalPages > 1 && (
                  <span className="ml-2 text-muted-foreground/70">
                    · Sivu {currentPage}/{totalPages}
                  </span>
                )}
              </p>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Järjestä:</span>
                <select
                  value={jarjesta}
                  onChange={(e) =>
                    navigate({
                      search: (prev: { jarjesta?: SortId; sivu?: number }) => ({ ...prev, jarjesta: e.target.value as SortId, sivu: 1 }),
                    })
                  }
                  className="rounded-full border border-border bg-background px-3 py-1.5 text-sm text-foreground focus:border-foreground focus:outline-none"
                  aria-label="Järjestä tuotteet"
                >
                  {SORT_IDS.map((id) => (
                    <option key={id} value={id}>
                      {sortLabel[id]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {products.map((p) => (
                <ProductCard key={p.node.id} product={p} />
              ))}
            </div>
            {totalPages > 1 && (
              <nav
                aria-label="Sivunumerointi"
                className="mt-12 flex flex-wrap items-center justify-center gap-2"
              >
                <button
                  type="button"
                  onClick={() =>
                    navigate({ search: (prev: { jarjesta?: SortId; sivu?: number }) => ({ ...prev, sivu: Math.max(1, currentPage - 1) }) })
                  }
                  disabled={currentPage === 1}
                  className="rounded-full border border-border px-4 py-2 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-40 hover:border-foreground"
                >
                  ← Edellinen
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => navigate({ search: (prev: { jarjesta?: SortId; sivu?: number }) => ({ ...prev, sivu: n }) })}
                    aria-current={n === currentPage ? "page" : undefined}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                      n === currentPage
                        ? "border-foreground bg-foreground text-background"
                        : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    navigate({
                      search: (prev: { jarjesta?: SortId; sivu?: number }) => ({ ...prev, sivu: Math.min(totalPages, currentPage + 1) }),
                    })
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-full border border-border px-4 py-2 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-40 hover:border-foreground"
                >
                  Seuraava →
                </button>
              </nav>
            )}
          </>
        )}
      </section>

      <section className="border-t border-border bg-cream/40">
        <div className="container-prose py-16 md:py-24 space-y-12 max-w-3xl">
          <div>
            <h2 className="text-3xl text-foreground text-balance">Miksi juuri nämä lelut?</h2>
            <div className="mt-5 space-y-4 leading-relaxed text-muted-foreground">
              <p>Wooden Story ja Trixie Baby ovat eurooppalaisia puulelujen valmistajia, jotka työstävät jokaisen lelun käsin FSC-sertifioidusta puusta ja viimeistelevät vesipohjaisilla väreillä. Ne täyttävät sen mitä Maria Montessori vaati leikkivälineiltä: yksi selkeä taito kerrallaan, kaunis muoto ja aitoa materiaalia.</p>
              <p>Uusi Montessori Lelut -osasto kokoaa nämä lelut yhteen paikkaan — helistimet vauvalle, pinoamis- ja lajittelulelut taaperolle, palapelit ja puiset roolileikkisetit esikouluikäiselle.</p>
            </div>
          </div>
          <div>
            <h2 className="text-3xl text-foreground">Usein kysyttyä — Montessori Lelut</h2>
            <dl className="mt-6 space-y-6">
              {FAQS.map((f) => (
                <div key={f.q}>
                  <dt className="text-lg text-foreground">{f.q}</dt>
                  <dd className="mt-2 leading-relaxed text-muted-foreground">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>
    </Layout>
  );
}
